/* eslint-disable prefer-promise-reject-errors */
import { DialogPlugin, DialogInstance } from 'tdesign-vue';
import i18n, { i18n_t } from '@/locales/i18n';
import store from '@/renderer/store/index';
import { getDeviceName, getUrlQuery } from '@/renderer/scripts/utils/tools/index';
import smh from '@/renderer/scripts/smh';
import tips from '@/renderer/scripts/utils/tips';
import user, { User } from '@/renderer/scripts/utils/user';
import meeting from '@/renderer/scripts/utils/meeting';
import { IS_BROWSER_IN_MOBILE } from '@/common/constant/platform';
import CustomDialog from '@/renderer/scripts/utils/CustomDialog';
import { dispatchRouter } from '@/renderer/scripts/utils/tools/dispatchRouter';

export interface RouterParams {
  path: string;
  fullPath: string;
  query: object;
}
export interface MeetingLoginParams {
  loginToPath: string;
  showTip: boolean;
  callback: Function;
}
interface LoginStatus {
  canLogin: boolean;
  needAuth: boolean;
  loginInfo: null | CoFile.LoginSMSData;
  error?: any;
}

// 注入登录态
const setLoginStatus = async (loginInfo: CoFile.LoginSMSData, callback?: Function): Promise<boolean> => {
  let setSuccessful = false;
  const orgList = loginInfo.organizations;
  const logined = await user.verifyAndLogin({
    userId: loginInfo.userId,
    userToken: loginInfo.userToken,
    isNewUser: loginInfo.isNewUser,
    hasAppliedChannelOrg: loginInfo.hasAppliedChannelOrg,
    organizations: orgList,
  });
  store.commit('setLoginingMask', false);
  user.setRemember(true);
  // verify成功且登录完成后直接return
  if (logined) {
    return true;
  }
  // 无法一步登录成功 需要停在登录页
  const { canAutoLoginOrg, cannotSelectOrg } = user.getUserInfoSync();
  setSuccessful = canAutoLoginOrg as boolean;
  if (!canAutoLoginOrg) {
    // 当前账号没有可用企业，需要停在登录页
    if (cannotSelectOrg) {
      user.setLoginType(User.LoginTypeKey.loginWithMeetingPhone);
      return setSuccessful;
    }
    // 需要回到登录页去选择企业
    const { pathname, search } = window.location;
    const routerName = pathname.includes('/meetingLogin') ? 'meetingLogin' : 'login';
    callback &&
      callback({
        name: routerName,
        query: {
          needSelectOrg: '1',
          path: routerName === 'login' ? `${pathname}${search}` : '',
        },
      })?.catch((e: any) => console.log(e));
  }
  return setSuccessful;
};

// 登陆进上次登录的企业
const loginToOrg = async (loginInfo: CoFile.LoginSMSData, loginToPath: string, callback: Function) => {
  const setSuccessful = await setLoginStatus(loginInfo, callback);
  if (!setSuccessful) return;
  if (loginToPath) {
    const { url, query: queryObj } = getUrlQuery(loginToPath as string);
    callback({
      path: url,
      query: queryObj,
    })?.catch((e: any) => console.log(e));
  } else {
    let routerName = IS_BROWSER_IN_MOBILE ? 'recentUse' : 'workbench';
    // 若登录时企业已过期自动跳转至过期页面
    if (store.getters.orgExpired) {
      routerName = 'teamExpired';
    }
    callback({
      name: routerName,
    })?.catch((e: any) => console.log(e));
  }
  let timer: any = setTimeout(() => {
    store.commit('setLoginingMask', false);
    clearTimeout(timer);
    timer = null;
  }, 500);
};

// 是否可以免登录
const getLoginStatusAPI = (): Promise<LoginStatus> =>
  new Promise((resolve, reject) => {
    meeting
      .requestAgentAuthCode()
      .then(resp => {
        const code = resp.authCode;
        const { loginFunc, invitationCode } = user.getUserInfoSync();
        const reqParams = {
          code,
          deviceId: getDeviceName(),
        };
        if (loginFunc === User.LoginFuncKey.loginToJoin) {
          // 会议里没有打开邀请码的场景，这里的逻辑目前进不去
          Object.assign(reqParams, {
            inviteCode: invitationCode,
          });
        }
        // 用免登授权码登录腾讯网盘
        smh.api.loginByMeeting(reqParams).then((res: CoFile.LoginSMSData) => {
          if (res.hasError) {
            if (res.code === 'UserNotBindPhoneNumber') {
              resolve({
                canLogin: false,
                needAuth: true,
                loginInfo: null,
              });
            } else {
              // 授权成功但不能登录腾讯网盘
              reject({
                error: res,
                canLogin: false,
                needAuth: false,
                loginInfo: null,
              });
            }
          } else {
            resolve({
              canLogin: true,
              needAuth: false,
              loginInfo: res,
            });
          }
        });
      })
      .catch(e => {
        reject({
          ...e,
          canLogin: false,
          needAuth: true,
          loginInfo: null,
        });
      });
  });

// 获取登录态
const getLoginStatus = async (): Promise<LoginStatus> => {
  const loginStatus = {
    canLogin: false,
    loginInfo: null,
    needAuth: false,
  };
  try {
    const result = await getLoginStatusAPI();
    const { canLogin, loginInfo, needAuth } = result;
    Object.assign(loginStatus, {
      canLogin,
      loginInfo,
      needAuth,
    });
  } catch (e: any) {
    Object.assign(loginStatus, {
      canLogin: false,
      loginInfo: null,
      needAuth: e.needAuth,
      error: e.error,
    });
  }
  return loginStatus;
};

/**
 * 提供出去的腾讯会议内免登方法
 * loginToPath: 登录后需要跳转的路由参数，比如/web/teamSpace,/web/share?qwezxwesa
 * showTip: 免登时是否弹tips
 * callback: (routeParams) => void,自行跳转路由
 */
export const meetingLoginFn = async ({ loginToPath, showTip = false, callback }: MeetingLoginParams): Promise<void> => {
  const loginStatus = await getLoginStatus();
  const { canLogin, loginInfo, needAuth, error } = loginStatus;
  if (canLogin && loginInfo) {
    // 可以自动登录
    showTip && tips.info(i18n_t('获取到免登码，准备登录'), 1000);
    /* i18n-ignore*/
    console.log('获取到免登码，准备登录');
    loginToOrg(loginInfo, loginToPath, callback);
  } else if (needAuth) {
    // 需要重新授权
    showTip && tips.info(i18n_t('获取免登码失败，准备进行重新授权'), 1000);
    /* i18n-ignore*/
    console.log('获取免登码失败，进行重新授权');
    user.setLoginAction(User.LoginActionKey.bindPhone);
    user.setLoginBindType(User.LoginBindTypeKey.bindPhoneInMeeting);
    user.setLoginType(User.LoginTypeKey.loginWithMeetingPhone);
    meeting.requestAuth(loginToPath);
  } else {
    store.commit('setLoginingMask', false);
    user.setLoginType(User.LoginTypeKey.loginWithMeetingPhone);
    // 需要前往登录页
    // 如果本身是去登录页就只报错
    if (loginToPath === '') {
      tips.error(error);
      return;
    }
    // 如果去往编辑页,就报错并返回登录页
    if (loginToPath.includes('/web/preview/edit')) {
      tips.error(error);
      callback({
        name: 'login',
        query: {
          logout: 1,
        },
      }).catch((e: any) => console.log(e));
    }
  }
};

// 文档编辑页需用户决定授权登录或者跳转到预览页
const loginToEdit = async (next: Function) => {
  try {
    const result = await getLoginStatus();
    const { canLogin, loginInfo, needAuth, error } = result;
    if (canLogin && loginInfo) {
      const setSuccessful = await setLoginStatus(loginInfo, next);
      if (!setSuccessful) return;
      next();
    } else if (needAuth || error) {
      // 需要会议授权或已知无法登录腾讯网盘,让用户手动选择
      const { href, pathname, search } = window.location;
      const dialog: DialogInstance = CustomDialog.confirm({
        theme: 'warning',
        header: i18n_t('提示'),
        body: i18n_t('当前账号未登录，请确认是否需要授权登录或者查看预览链接'),
        width: '400px',
        closeBtn: false,
        closeOnEscKeydown: false,
        closeOnOverlayClick: false,
        confirmBtn: i18n_t('授权登录'),
        cancelBtn: i18n_t('查看预览'),
        // 去登录
        async onConfirm() {
          if (dialog?.hide) {
            dialog.hide();
          }
          meetingLoginFn({
            loginToPath: `${pathname}${search}`,
            showTip: false,
            callback: next,
          });
        },
        // 去预览
        onCancel() {
          if (dialog?.hide) {
            dialog.hide();
          }
          // 重定向到预览链接
          dispatchRouter(href.replace(/web\/preview\/edit/gim, 'web/preview/doc'), 'location');
        },
      });
    }
  } catch (e) {
    tips.error(e as MsgError);
    logger.ui('requestAgentAuthCode error', e);
    // 免登失败，重新授权
    const { pathname, search } = window.location;
    user.setLoginAction(User.LoginActionKey.bindPhone);
    user.setLoginBindType(User.LoginBindTypeKey.bindPhoneInMeeting);
    meeting.requestAuth(`${pathname}${search}`);
  }
};

// 腾讯会议里自动登录
export const meetingLoginInRouter = async (to: RouterParams, from: RouterParams, next: Function): Promise<void> => {
  const isLogining = from.path === '/' && to.path === '/login';
  const isLogout = from.path !== '/' && to.path === '/login';
  const isAuthing = to.path === '/web/meetingLogin';
  const isPreviewing = ['/web/preview/doc', '/web/preview/media'].includes(to.path);
  const isEditing = to.path === '/web/preview/edit';
  const isSharing = to.path.includes('/web/share/');
  const userInfo = user.getUserInfo();
  const isLogined = store.getters.isLogined || userInfo.isLogined;
  /**
   * 不需要登录的场景，直接进入路由
   * 1、已登录
   * 2、正在登录或退出登录交给login组件处理
   * 3、绑定会议授权
   * * */
  if (isLogined || isLogining || isLogout || isAuthing) {
    next();
    return;
  }
  // 预览页、分享页无论登录成功与否都可以进入
  if (isPreviewing || isSharing) {
    const { loginInfo } = await getLoginStatus();
    loginInfo && (await setLoginStatus(loginInfo, next));
    next();
    return;
  }
  // 编辑页可选择去预览或去授权
  if (isEditing) {
    loginToEdit(next);
    return;
  }
  // 其他页面必须登录才能进入，否则进入授权
  meetingLoginFn({
    loginToPath: to.fullPath,
    showTip: false,
    callback: next,
  });
};

// 会议里授权并登录到当前路由
export const authAndLogin = (callback: Function): void => {
  const { pathname, search } = window.location;
  const redirectPath = `${pathname}${search}`;
  meetingLoginFn({
    loginToPath: redirectPath,
    showTip: false,
    callback: (params: any) => callback(params),
  });
};

// 使用会议里自动获取手机号并登录
export const meetingPhoneLogin = async () => {
  try {
    const meetingToken = await meeting.getPhoneNumber();
    tips.loading(i18n_t('正在登录，请稍后'), 0);
    const { authCode } = await meeting.requestAgentAuthCode();
    const reqParams = {
      code: authCode,
      deviceId: getDeviceName(),
      meetingToken,
    };
    const loginInfo = await smh.api.loginByMeeting(reqParams);
    if (loginInfo.hasError) {
      tips.error(i18n_t('登录失败'));
      return;
    }
    user.setLoginType(User.LoginTypeKey.loginWithMeetingPhone);
    // 只生成登录态 由组件内部做登录成功的逻辑
    const setSuccessful = await setLoginStatus(loginInfo);
    if (!setSuccessful) tips.error(i18n_t('登录失败'));
  } catch (e: any) {
    tips.error(i18n_t('登录失败'));
  }
};
